import axios from "axios"
import { API_TIMEOUT_MS } from "constants/apiTimeout"
import { createHmacSha256, generateStrongKey } from "common/utils"
import { trimDeep } from "utils/formUtils"
import { redirectToMaintenance, isMaintenanceResponse } from "utils/maintenanceRedirect"

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1/"
const HMAC_SECRET_KEY = process.env.REACT_APP_HMAC_SECRET_KEY || ""

export const ONBOARDING_TOKEN_KEY = "onboardingToken"
export const ONBOARDING_TENANT_KEY = "onboardingTenantId"
export const ONBOARDING_PLAN_KEY = "onboardingPlanId"
export const ONBOARDING_PLAN_SNAPSHOT_KEY = "onboardingPlanSnapshot"
export const ONBOARDING_SUBSCRIPTION_KEY = "onboardingSubscriptionId"
export const ONBOARDING_INVOICE_KEY = "onboardingInvoiceId"

const onboardingApi = axios.create({
  baseURL: API_URL,
  timeout: API_TIMEOUT_MS,
})

onboardingApi.interceptors.request.use(async config => {
  const token =
    typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem(ONBOARDING_TOKEN_KEY)
      : null
  if (token) {
    config.headers.Authorization = token
  }

  const language =
    typeof localStorage !== "undefined"
      ? localStorage.getItem("I18N_LANGUAGE")
      : null
  config.headers["Accept-Language"] = language === "sa" ? "ar" : "en"

  const method = config.method ? config.method.toUpperCase() : null
  if (method === "PUT" || method === "PATCH" || method === "POST") {
    try {
      if (config.data instanceof FormData) {
        config.data = trimDeep(config.data)
      } else if (config.data && typeof config.data === "object") {
        config.data = trimDeep(config.data)
      } else if (typeof config.data === "string") {
        config.data = config.data.trim()
      }

      const requestBody = config.data || {}
      let bodyString = ""
      if (typeof requestBody === "string") {
        bodyString = requestBody
      } else if (requestBody && typeof requestBody === "object") {
        bodyString = JSON.stringify(requestBody)
      }
      if (bodyString && HMAC_SECRET_KEY) {
        const hash = await createHmacSha256(HMAC_SECRET_KEY, bodyString)
        if (hash) {
          config.headers["X-Signature"] = hash
        }
      }
    } catch (error) {
      console.error("Onboarding HMAC error:", error)
    }
  } else if (method === "GET" || method === "DELETE") {
    try {
      const generatedKey = generateStrongKey()
      config.headers["x-st"] = generatedKey
      if (HMAC_SECRET_KEY) {
        const hash = await createHmacSha256(HMAC_SECRET_KEY, generatedKey)
        if (hash) {
          config.headers["X-Signature"] = hash
        }
      }
    } catch (error) {
      console.error("Onboarding HMAC error:", error)
    }
  }

  return config
})

onboardingApi.interceptors.response.use(
  response => response,
  error => {
    if (isMaintenanceResponse(error)) {
      redirectToMaintenance(error)
    }
    return Promise.reject(error)
  }
)

export function onboardingGet(url, config = {}) {
  return onboardingApi.get(url, config).then(res => res.data)
}

export function onboardingPost(url, data = {}, config = {}) {
  return onboardingApi
    .post(url, data, config)
    .then(res => res.data)
    .catch(err => {
      if (err?.response?.data) return err.response.data
      throw err
    })
}

export const getOnboardingApiMessage = res => {
  if (!res) {
    return ""
  }
  if (typeof res.msg === "string" && res.msg.trim()) {
    return res.msg.trim()
  }
  if (typeof res.message === "string" && res.message.trim()) {
    return res.message.trim()
  }
  if (Array.isArray(res.errors) && res.errors.length > 0) {
    return res.errors
      .map(item => (typeof item === "string" ? item : item?.message || ""))
      .filter(Boolean)
      .join(", ")
  }
  if (typeof res.error === "string" && res.error.trim()) {
    return res.error.trim()
  }
  return ""
}

export const saveOnboardingRegistration = ({ onboardingToken, tenant } = {}) => {
  if (onboardingToken) {
    sessionStorage.setItem(ONBOARDING_TOKEN_KEY, onboardingToken)
  }
  const tenantId = tenant?._id || tenant?.id
  if (tenantId) {
    sessionStorage.setItem(ONBOARDING_TENANT_KEY, String(tenantId))
  }
}

export const hasOnboardingRegistration = () =>
  Boolean(
    sessionStorage.getItem(ONBOARDING_TOKEN_KEY) &&
      sessionStorage.getItem(ONBOARDING_TENANT_KEY)
  )

export const clearOnboardingSession = () => {
  sessionStorage.removeItem(ONBOARDING_TOKEN_KEY)
  sessionStorage.removeItem(ONBOARDING_TENANT_KEY)
  sessionStorage.removeItem(ONBOARDING_PLAN_KEY)
  sessionStorage.removeItem(ONBOARDING_PLAN_SNAPSHOT_KEY)
  sessionStorage.removeItem(ONBOARDING_SUBSCRIPTION_KEY)
  sessionStorage.removeItem(ONBOARDING_INVOICE_KEY)
}

export const getOnboardingToken = () => sessionStorage.getItem(ONBOARDING_TOKEN_KEY)

export const getOnboardingTenantId = () => sessionStorage.getItem(ONBOARDING_TENANT_KEY)

export const getOnboardingPlanId = () => sessionStorage.getItem(ONBOARDING_PLAN_KEY)
